/**
 * MediaSession API wrapper — lets the OS lock screen + hardware media keys
 * control PrepMasters' Read Aloud Mode while it's running.
 *
 * History: previously we ran a tiny silent WAV loop in parallel with TTS
 * because Web SpeechSynthesis didn't claim Android's audio focus. That
 * loop was the source of a periodic "thud" on iOS/Android/desktop —
 * the base64 blob was 20 ms of DC-offset 8-bit unsigned PCM (all 0x00
 * bytes, where true silence would be 0x80), so every loop restart
 * produced an audible click.
 *
 * Since the Read Aloud engine now streams real OpenAI MP3s through an
 * HTML5 <audio> element, actual audio output already claims the OS audio
 * focus and populates the lock-screen card — the silent loop is no
 * longer needed and is fully removed.
 *
 * `startSilentLoop` / `stopSilentLoop` remain exported as no-ops so any
 * caller that still imports them keeps working during the migration.
 */

export function isMediaSessionSupported() {
  return typeof navigator !== "undefined" && "mediaSession" in navigator;
}

// Kept as no-ops for backwards compatibility with existing imports.
// The real <audio> element in tts.js now provides audio focus.
export function startSilentLoop() { /* noop — see file header */ }
export function stopSilentLoop() { /* noop — see file header */ }

export function setMediaMetadata({ title, artist, album }) {
  if (!isMediaSessionSupported() || !window.MediaMetadata) return;
  try {
    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: (title || "PrepMasters").slice(0, 140),
      artist: artist || "PrepMasters · Read Aloud Mode",
      album: album || "PrepMasters",
      artwork: [
        { src: "/icon.svg", sizes: "192x192", type: "image/svg+xml" },
        { src: "/icon-maskable.svg", sizes: "512x512", type: "image/svg+xml" },
      ],
    });
  } catch { /* noop */ }
}

export function setPlaybackState(state) {
  if (!isMediaSessionSupported()) return;
  try {
    // Valid: "none" | "paused" | "playing"
    navigator.mediaSession.playbackState = state;
  } catch { /* noop */ }
}

const REGISTERED_ACTIONS = ["play", "pause", "stop", "nexttrack", "previoustrack", "seekforward", "seekbackward"];

export function setMediaHandlers(handlers) {
  if (!isMediaSessionSupported()) return;
  REGISTERED_ACTIONS.forEach((a) => {
    try {
      navigator.mediaSession.setActionHandler(a, handlers[a] || null);
    } catch { /* unsupported action — ignore */ }
  });
}

export function clearMediaSession() {
  if (!isMediaSessionSupported()) return;
  try {
    REGISTERED_ACTIONS.forEach((a) => {
      try { navigator.mediaSession.setActionHandler(a, null); } catch { /* noop */ }
    });
    navigator.mediaSession.metadata = null;
    navigator.mediaSession.playbackState = "none";
  } catch { /* noop */ }
}
